import { ConvertCentimetersToMillimeters, ConvertMillimetersToCentimeters } from '@/utils/converters';
import Select from '@/components/DSE/Select';
import Switch from '@/components/DSE/Switch';
import { validatorRequire, validatorRequireNumber } from '@/utils/validators';
import PickRec from '@/components/DSE/PickRec';
import MDinput from '@/components/MDinput';
import { ProductGroupTypeOption } from '@/utils/constants';
import DateTimePicker from '@/components/DSE/DateTimePicker';
import OrderLineHeader from './components/OrderLineHeader';
import { OrderLineStatusOption, ExceedanceOption } from '@/utils/constants';
import FamilyOrderList from './components/FamilyOrderList';
import OrderLineDetails from './components/OrderLineDetails';
import EmbraceInfo from './components/EmbraceInfo';
import OrderLinePrices from './components/orderlineprices/index';
import OrderLineReturnReasonSelection from './components/OrderLineReturnReasonSelection';
import { toISOString } from '@/utils/date';
import HistoryViewer from '@/pages/orderlines/components/HistoryViewer';
import TextArea from '@/components/DSE/TextArea';
import AddressInput from '@/components/DSE/AddressInput';
import Link from '@/components/DSE/Link';

export function getFormDefinition(vue) {
  return {
    type: 'tab',
    saveButtons: [true, false, false, false, false, false], // Page, tab 1, tab 2, ...
    showFormLabels: [true, true, true, false, false], // Tab 1, tab 2, ....
    labels: [
      'orderLine.tabGeneral',
      'orderLine.familyOrder',
      'return.return',
      'orderLine.tabPrices',
      'orderLine.tabHistory'
    ],
    header: form => {
      return {
        type: OrderLineHeader,
        props: {
          orderInfo: form
        }
      };
    },
    groups: [
      {
        productGroupDescription: {
          type: MDinput,
          defaultValue: '',
          props: {
            caption: 'orderLine.productGroup',
            nofocus: true,
            readonly: true
          }
        },
        status: {
          type: Select,
          defaultValue: OrderLineStatusOption.New,
          props: {
            caption: 'orderLine.orderLineStatus',
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('orderLineStatus'),
            readonly: true
          },
          valueOnFormChange: (form, oldValue) => {
            const orderLineStatus = vue.$store.getters['orderLineStatus/getLoadedItemByID'](form.orderLineStatusID);
            return orderLineStatus && orderLineStatus.orderLineStatusOption;
          }
        },
        orderLineStatusID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'orderLine.orderLineSubstatus',
            valueField: 'id',
            labelField: 'name',
            loading: vue.$store.getters['orderLineStatus/isLoading'],
            required: true,
            readonly: false
          },
          dynamicProps: form => {
            return {
              options: vue.$store.getters['orderLineStatus/loadedItems'].filter(
                s => s.settableYN || s.id === form.orderLineStatusID
              )
            };
          },
          rules: [{ validator: validatorRequireNumber }]
        },
        validationMessage: {
          type: TextArea,
          props: {
            caption: 'orderLine.validationMessage',
            placeholder: 'common.emptyString',
            minRows: 1,
            maxRows: 6,
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: vue.orderIsUnprocessable
            };
          }
        },
        orderExceedance: {
          type: Select,
          defaultValue: ExceedanceOption.None,
          props: {
            caption: 'orderLine.exceedance',
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('orderExceedance'),
            readonly: true
          },
          rules: [{ validator: validatorRequireNumber }]
        },
        room: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'orderLine.room',
            maxlength: 150,
            readonly: true
          }
        },
        estimatedDeliveryDate: {
          type: DateTimePicker,
          defaultValue: null,
          props: {
            caption: 'orderLine.estimatedDeliveryDate',
            type: 'date',
            format: vue.$store.getters.dateFormat
          },
          dynamicProps: form => {
            return {
              readonly: vue.orderIsCancelled
            };
          }
        },
        transportOrderID: {
          type: Link,
          props: {
            caption: 'common.shipmentList',
            entity: 'TransportOrder'
          },
          dynamicProps: form => {
            return {
              label: vue.$t('orderLine.toShipmentList')
            };
          }
        },
        remarks: {
          type: TextArea,
          defaultValue: null,
          props: {
            caption: 'orderLine.remarksOnTicket',
            minRows: 1,
            placeholder: 'orderLine.remarksOnTicket'
          }
        },
        fabricProductID: {
          type: PickRec,
          defaultValue: null,
          props: {
            caption: 'fabric.description',
            valueField: 'id',
            labelField: 'domainProductCode',
            inputField: 'description',
            maxlength: 64,
            columns: [
              { field: 'description', label: 'common.description' },
              { field: 'productTypeDescription', label: 'fabric.productType' },
              { field: 'domainProductCode', label: 'fabric.domainProductCode' }
            ],
            entity: 'Fabric',
            required: true,
            usePagination: true,
            hideInput: false,
            suggest: false,
            readonly: true
          },
          rules: [{ validator: validatorRequire }]
        },
        actualFabricWidthInCm: {
          type: MDinput,
          defaultValue: 0,
          props: {
            caption: 'orderLine.fabricWidth',
            type: 'number',
            precision: 0,
            readonly: true
          }
        },
        patternHeightInMm: {
          type: MDinput,
          defaultValue: 0,
          props: {
            caption: 'orderLine.patternHeight',
            type: 'number',
            precision: 0,
            readonly: true
          }
        },
        liningFabricProductID: {
          type: PickRec,
          defaultValue: null,
          props: {
            caption: 'fabric.description',
            valueField: 'id',
            labelField: 'domainProductCode',
            inputField: 'description',
            maxlength: 64,
            columns: [
              { field: 'description', label: 'common.description' },
              { field: 'productTypeDescription', label: 'fabric.productType' },
              { field: 'domainProductCode', label: 'fabric.domainProductCode' }
            ],
            entity: 'Fabric',
            filters: {
              canBeUsedAsLiningYN: true
            },
            usePagination: true,
            hideInput: false,
            suggest: false,
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: vue.liningApplicable
            };
          }
        },
        liningFabricActualWidthInCm: {
          type: MDinput,
          defaultValue: 0,
          props: {
            caption: 'orderLine.fabricWidth',
            type: 'number',
            precision: 0,
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: vue.liningApplicable
            };
          }
        },
        liningFabricPatternHeightInMm: {
          type: MDinput,
          defaultValue: 0,
          props: {
            caption: 'orderLine.patternHeight',
            type: 'number',
            precision: 0,
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: vue.liningApplicable
            };
          }
        },
        finishedHeight: {
          type: MDinput,
          props: { type: 'number' },
          defaultValue: null,
          dynamicProps: form => {
            return vue.getFinishedHeightProps(vue.isEdit, form.returnOrderLineYN);
          }
        },
        numberOfRibs: {
          type: MDinput,
          defaultValue: null,
          props: {
            type: 'number',
            caption: 'orderLine.numberOfRibs',
            precision: 0,
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType(ProductGroupTypeOption.RomanBlind)
            };
          }
        },
        finishedWidthLeft: {
          type: MDinput,
          defaultValue: null,
          props: { type: 'number', caption: 'common.emptyString' },
          dynamicProps: form => {
            return vue.getFinishedWidthBySideProps('Left', form.finishedWidthLeft, vue.isEdit, form.returnOrderLineYN);
          }
        },
        finishedWidthRight: {
          type: MDinput,
          defaultValue: null,
          props: { type: 'number', caption: 'common.emptyString' },
          dynamicProps: form => {
            return vue.getFinishedWidthBySideProps(
              'Right',
              form.finishedWidthRight,
              vue.isEdit,
              form.returnOrderLineYN
            );
          }
        },
        railLength: {
          type: MDinput,
          defaultValue: null,
          props: {
            type: 'number',
            caption: 'orderLine.railLength',
            precision: 1,
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible:
                vue.isProductGroupType(ProductGroupTypeOption.Curtain) ||
                vue.isProductGroupType(ProductGroupTypeOption.NetCurtain)
            };
          }
        },
        waveSize: {
          type: MDinput,
          props: { type: 'number', readonly: true, caption: 'orderLine.waveSize' },
          defaultValue: null,
          dynamicProps: form => {
            return {
              visible: !!form.waveSize
            };
          }
        },
        numberOfPanels: {
          type: MDinput,
          defaultValue: null,
          props: {
            type: 'number',
            caption: 'orderLine.numberOfPanels',
            readonly: true,
            precision: 2
          },
          dynamicProps: form => {
            return {
              visible: vue.productGroupHasPanels
            };
          },
          valueOnFormChange: (form, oldValue) => {
            return vue.getNumberOfPanels(form);
          }
        },
        numberOfPanelsLeft: {
          type: MDinput,
          defaultValue: null,
          props: { type: 'number', readonly: true },
          dynamicProps: form => {
            return vue.getNumberOfPanelsPropsBySideProps('Left', false);
          }
        },
        numberOfPanelsRight: {
          type: MDinput,
          defaultValue: null,
          props: { type: 'number', readonly: true },
          dynamicProps: form => {
            return vue.getNumberOfPanelsPropsBySideProps('Right', false);
          }
        },
        cutSize: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'orderLine.cutSize',
            readonly: true,
            type: 'number',
            precision: 1,
            step: 0.5
          },
          dynamicProps: form => {
            return {
              visible: !!vue.getCutSize(form)
            };
          },
          valueOnFormChange: (form, oldValue) => {
            return vue.getCutSize(form);
          }
        },
        requiredFabric: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'orderLine.totalFabricInCm',
            readonly: true,
            type: 'number',
            precision: 1
          }
        },
        fabricPurchasePrice: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'fabric.purchasePrice',
            readonly: true
          }
        },
        fabricConsumerPrice: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'priceListItemPrice.consumerPrice',
            readonly: true
          }
        },
        numberOfPanelsLining: {
          type: MDinput,
          defaultValue: null,
          props: {
            type: 'number',
            caption: 'orderLine.numberOfPanels',
            readonly: true,
            precision: 1
          },
          dynamicProps: form => {
            return {
              visible: vue.productGroupHasPanels && vue.liningApplicable
            };
          },
          valueOnFormChange: (form, oldValue) => {
            return vue.getNumberOfPanelsLining(form);
          }
        },
        numberOfPanelsLeftLining: {
          type: MDinput,
          defaultValue: null,
          props: { type: 'number', readonly: true },
          dynamicProps: form => {
            return vue.getNumberOfPanelsPropsBySideProps('Left', true);
          }
        },
        numberOfPanelsRightLining: {
          type: MDinput,
          defaultValue: null,
          props: { type: 'number', readonly: true },
          dynamicProps: form => {
            return vue.getNumberOfPanelsPropsBySideProps('Right', true);
          }
        },
        cutSizeLining: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'orderLine.cutSize',
            readonly: true,
            type: 'number',
            precision: 1,
            step: 0.5
          },
          dynamicProps: form => {
            return {
              visible: vue.liningApplicable && !!vue.getCutSizeLining(form)
            };
          },
          valueOnFormChange: (form, oldValue) => {
            return vue.getCutSizeLining(form);
          }
        },
        requiredLiningFabric: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'orderLine.totalFabricInCm',
            readonly: true,
            type: 'number',
            precision: 1
          },
          dynamicProps: form => {
            return {
              visible: vue.liningApplicable
            };
          }
        },
        liningFabricPurchasePrice: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'fabric.purchasePrice',
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: vue.liningApplicable
            };
          }
        },
        liningFabricConsumerPrice: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'priceListItemPrice.consumerPrice',
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: vue.liningApplicable
            };
          }
        },
        headingHeight: {
          type: MDinput,
          props: { type: 'number', readonly: true },
          defaultValue: null,
          dynamicProps: form => {
            return vue.getHeadingHeightProps();
          }
        },
        operationSide: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'orderLine.operationSide',
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('operationSide'),
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType(ProductGroupTypeOption.RomanBlind) || vue.hasFinishTypeOperation()
            };
          }
        },
        ribsEqual: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'orderLine.ribsEqual',
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('ribsEqual'),
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType(ProductGroupTypeOption.RomanBlind)
            };
          }
        },
        highestInSet: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'orderLine.highestInSet',
            type: 'number',
            precision: 1
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType(ProductGroupTypeOption.RomanBlind),
              readonly: !form.returnOrderLineYN
            };
          }
        },
        ribsType: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'orderLine.ribsType',
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('romanBlindRibType'),
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType(ProductGroupTypeOption.RomanBlind)
            };
          }
        },
        ribsSide: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'orderLine.ribsSide',
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('ribsSide'),
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType(ProductGroupTypeOption.RomanBlind)
            };
          }
        },
        assemblyHeight: {
          type: MDinput,
          defaultValue: null,
          props: {
            type: 'number',
            caption: 'orderLine.assemblyHeight',
            precision: 1,
            step: 0.5,
            readonly: false
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType(ProductGroupTypeOption.RomanBlind)
            };
          }
        },
        numberOfSupports: {
          type: MDinput,
          defaultValue: null,
          props: {
            type: 'number',
            caption: 'orderLine.numberOfSupports',
            precision: 0,
            step: 1,
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType(ProductGroupTypeOption.RomanBlind) || vue.hasFinishTypeOperation()
            };
          }
        },
        numberOfCords: {
          type: MDinput,
          defaultValue: null,
          props: {
            type: 'number',
            caption: 'orderLine.numberOfCords',
            precision: 0,
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: vue.isProductGroupType(ProductGroupTypeOption.RomanBlind)
            };
          }
        },
        numberOfParcels: {
          type: MDinput,
          defaultValue: 0,
          props: {
            caption: 'orderLine.numberOfParcels',
            readonly: true,
            type: 'number',
            precision: 2
          }
        },
        shareHalfPanelWith: {
          type: MDinput,
          defaultValue: null,
          props: {
            readonly: true,
            caption: 'orderLine.shareHalfPanelWith'
          },
          dynamicProps: form => {
            return {
              visible: vue.productGroupHasPanels
            };
          }
        },
        shareHangerWith: {
          type: MDinput,
          defaultValue: null,
          props: {
            readonly: true,
            caption: 'orderLine.shareHangerWith'
          }
        },
        internalRemarks: {
          type: TextArea,
          defaultValue: null,
          props: {
            caption: 'order.internalRemarks',
            minRows: 1,
            placeholder: 'order.internalRemarks'
          }
        },
        customerReference: {
          type: MDinput,
          defaultValue: '',
          props: {
            caption: 'orderLine.customerReference',
            maxlength: 64,
            required: true
          },
          dynamicProps: form => ({
            readonly: !form.returnOrderLineYN
          })
        },
        returnOrderLineYN: {
          type: Switch,
          defaultValue: false,
          props: {
            activeText: 'common.yes',
            inactiveText: 'common.no',
            caption: 'common.returnOrder'
          },
          dynamicProps: form => ({
            visible: form.interiorDecoratorOrderYN === true,
            disabled: vue.orderIsInProductionOrCancelled
          })
        },
        consumerName: {
          type: MDinput,
          defaultValue: '',
          props: {
            caption: 'order.consumerName',
            maxlength: 64,
            readonly: true
          }
        },
        takeLeftoverFabricYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'orderLine.takeLeftoverFabric',
            readonly: false
          }
        },
        storeID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'common.store',
            valueField: 'id',
            labelField: 'codeAndName',
            loading: vue.$store.getters['store/isLoading']
          },
          dynamicProps: form => {
            const domainID = (form.order && form.order.domainID) || 0;
            return {
              options: vue.$store.getters['store/loadedItems'].filter(i => i.domainID === domainID)
            };
          }
        },
        alternativePickUpStore: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'orderLine.pickUpStore',
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: !!form.alternativePickUpStore
            };
          }
        },
        deliverAtHomeYN: {
          type: Switch,
          defaultValue: null,
          props: {
            activeText: 'orderLine.deliver',
            inactiveText: 'orderLine.pickUp',
            caption: 'orderLine.delivery'
          },
          dynamicProps: form => ({
            readonly: vue.isEdit && !form.returnOrderLineYN
          })
        },
        consumerAddress: {
          type: AddressInput,
          defaultValue: '',
          props: {
            caption: 'order.consumerAddress',
            countries: vue.$store.getters['country/loadedItems'],
            countriesLoading: vue.$store.getters['country/isLoading'],
            compact: true
          },
          dynamicProps: form => ({
            disabled: !(form.interiorDecoratorOrderYN || (form.returnOrderLineYN && form.deliverAtHomeYN))
          })
        },
        orderedAt: {
          type: DateTimePicker,
          hideOnCreate: true,
          props: {
            caption: 'order.orderedAt',
            readonly: true
          }
        },
        createdAt: {
          type: DateTimePicker,
          hideOnCreate: true,
          props: {
            caption: 'order.createdAt',
            readonly: true
          }
        },
        actualDeliveryDate: {
          type: DateTimePicker,
          defaultValue: null,
          props: {
            caption: 'orderLine.actualDeliveryDate',
            type: 'date',
            format: vue.$store.getters.dateFormat,
            readonly: true
          }
        },
        embraceForOrderNumber: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'orderLine.embraceForOrderNumber',
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: !!form.embraceForOrderNumber
            };
          }
        },
        embraceLengthLeft: {
          type: MDinput,
          defaultValue: null,
          props: {
            type: 'number',
            caption: 'orderLine.embraceLengthLeft',
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: !!form.embraceFinishID && !form.embraceFromOrderLine
            };
          }
        },
        embraceLengthRight: {
          type: MDinput,
          defaultValue: null,
          props: {
            type: 'number',
            caption: 'orderLine.embraceLengthRight',
            readonly: true
          },
          dynamicProps: form => {
            return {
              visible: !!form.embraceFinishID && !form.embraceFromOrderLine
            };
          }
        },
        embraceFromOrderLine: {
          type: EmbraceInfo,
          props: {
            caption: 'orderLine.embraceFromOrderLine'
          },
          dynamicProps: form => {
            return {
              visible: !!form.embraceFromOrderLine
            };
          }
        },
        orderLineDetails: {
          type: OrderLineDetails,
          props: {
            caption: 'orderLine.details'
          },
          dynamicProps: form => {
            return {
              visible: form.orderLineDetails && form.orderLineDetails.length > 0
            };
          }
        },
        remarksOnTicket: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'orderLine.systemRemarks',
            readonly: true
          },
          valueOnFormChange: (form, oldValue) => {
            return vue.getRemarksOnTicket(form);
          }
        },
        specials: {
          type: TextArea,
          defaultValue: null,
          props: {
            caption: 'orderLine.specials',
            readonly: true,
            minRows: 1,
            placeholder: 'orderLine.specials'
          }
        },
        groupReference: {
          type: MDinput,
          defaultValue: '',
          props: {
            caption: 'orderLine.groupReference',
            readonly: true,
            maxlength: 64
          }
        },
        groupTotal: {
          type: MDinput,
          defaultValue: null,
          props: {
            caption: 'orderLine.groupTotal',
            readonly: true,
            type: 'number',
            precision: 0,
            step: 1
          }
        }
      },
      {
        // tab 2 family orders
        familyOrderLines: {
          type: FamilyOrderList,
          defaultValue: [],
          props: {
            caption: 'orderLine.familyOrderLines'
          }
        }
      },
      {
        // tab 3 Prices
        orderLinePrices: {
          type: OrderLinePrices,
          defaultValue: [],
          props: {
            caption: 'common.emptyString'
          },
          dynamicProps: form => {
            return {
              invoicedYN: form.status === OrderLineStatusOption.Invoicing,
              returnOrderLineYN: form.returnOrderLineYN,
              interiorDecoratorOrderYN: form.interiorDecoratorOrderYN
            };
          }
        }
      },
      {
        orderLineLogs: {
          type: HistoryViewer,
          defaultValue: [],
          props: {
            caption: 'common.emptyString'
          }
        }
      },
      {
        returnCauseID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'return.returnCause',
            required: true,
            valueField: 'id',
            labelField: 'name',
            loading: vue.$store.getters['returnCause/isLoading'],
            options: vue.$store.getters['returnCause/loadedItems']
          }
        },
        returnType: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'return.returnType',
            required: true,
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('returnType')
          }
        },
        orderLineReturnReasonSelection: {
          type: OrderLineReturnReasonSelection,
          props: {
            caption: 'return.returnReason'
          },
          dynamicProps: form => ({
            currentReturnReasonID: form.returnReasonID
          })
        }
      }
    ]
  };
}

export function createItemFromFormData(vue, form, returnYN) {
  const item = {
    id: form.id,
    remarks: form.remarks,
    internalRemarks: form.internalRemarks,
    takeLeftoverFabricYN: form.takeLeftoverFabricYN,
    orderLineStatusID: form.orderLineStatusID,
    estimatedDeliveryDate: toISOString(form.estimatedDeliveryDate),
    assemblyHeightInMm: ConvertCentimetersToMillimeters(form.assemblyHeight),
    storeID: form.storeID
  };

  if (form.interiorDecoratorOrderYN || returnYN || form.returnOrderLineYN) {
    item.returnOrderLineYN = form.returnOrderLineYN;
    item.consumerAddressStreetName = form.consumerAddress.streetName;
    item.consumerAddressNumber = form.consumerAddress.number;
    item.consumerAddressPostalCode = form.consumerAddress.postalCode;
    item.consumerAddressCity = form.consumerAddress.city;
    item.consumerAddressCountryID = form.consumerAddress.countryID;
  }

  if (returnYN || form.returnOrderLineYN) {
    item.customerReference = form.customerReference;
    item.orderLineID = form.id;
    item.finishedHeightInMm = ConvertCentimetersToMillimeters(form.finishedHeight);
    item.finishedWidthLeftInMm = ConvertCentimetersToMillimeters(form.finishedWidthLeft);
    item.finishedWidthRightInMm = ConvertCentimetersToMillimeters(form.finishedWidthRight);
    item.highestHeightInSetInMm = ConvertCentimetersToMillimeters(form.highestInSet);
    item.returnReasonID = form.returnReasonID;
    item.returnType = form.returnType;
    item.returnCauseID = form.returnCauseID;
    item.deliverAtHomeYN = form.deliverAtHomeYN;
  }

  return item;
}

export function createFormDataFromItem(vue, item, returnYN) {
  const form = {
    ...item,
    ...item.order,
    finishedWidth: ConvertMillimetersToCentimeters(item.finishedWidthInMm),
    finishedHeight: ConvertMillimetersToCentimeters(item.finishedHeightInMm),
    finishedWidthLeft: ConvertMillimetersToCentimeters(item.finishedWidthLeftInMm),
    finishedWidthRight: ConvertMillimetersToCentimeters(item.finishedWidthRightInMm),
    railLength: ConvertMillimetersToCentimeters(item.railLengthInMm),
    assemblyHeight: ConvertMillimetersToCentimeters(item.assemblyHeightInMm),
    cutSizeLeft: ConvertMillimetersToCentimeters(item.cutSizeLeftInMm),
    cutSizeRight: ConvertMillimetersToCentimeters(item.cutSizeRightInMm),
    cutSizeLeftLining: ConvertMillimetersToCentimeters(item.cutSizeLeftLiningInMm),
    cutSizeRightLining: ConvertMillimetersToCentimeters(item.cutSizeRightLiningInMm),
    headingHeight: ConvertMillimetersToCentimeters(item.headingHeightInMm),
    embraceLengthLeft: ConvertMillimetersToCentimeters(item.embraceLengthLeftInMm),
    embraceLengthRight: ConvertMillimetersToCentimeters(item.embraceLengthRightInMm),
    waveSize: ConvertMillimetersToCentimeters(item.waveSizeInMm),
    actualFabricWidthInCm: ConvertMillimetersToCentimeters(item.actualFabricWidthInMm),
    liningFabricActualWidthInCm: ConvertMillimetersToCentimeters(item.liningFabricActualWidthInMm),
    requiredFabric: ConvertMillimetersToCentimeters(item.requiredFabricInMm),
    requiredLiningFabric: ConvertMillimetersToCentimeters(item.requiredLiningFabricInMm),
    highestInSet: ConvertMillimetersToCentimeters(item.highestHeightInSetInMm),
    consumerAddress: {
      streetName: item.order.consumerAddressStreetName,
      number: item.order.consumerAddressNumber,
      postalCode: item.order.consumerAddressPostalCode,
      city: item.order.consumerAddressCity,
      countryID: item.order.consumerAddressCountryID
    }
  };

  if (form.fabricPurchasePrice) {
    form.fabricPurchasePrice = `€ ${form.fabricPurchasePrice.toFixed(2)}`;
  }
  if (form.fabricConsumerPrice) {
    form.fabricConsumerPrice = `€ ${form.fabricConsumerPrice.toFixed(2)}`;
  }
  if (form.liningFabricPurchasePrice) {
    form.liningFabricPurchasePrice = `€ ${form.liningFabricPurchasePrice.toFixed(2)}`;
  }
  if (form.liningFabricConsumerPrice) {
    form.liningFabricConsumerPrice = `€ ${form.liningFabricConsumerPrice.toFixed(2)}`;
  }

  delete form.finishedWidthInMm;
  delete form.finishedHeightInMm;
  delete form.finishedWidthLeftInMm;
  delete form.finishedWidthRightInMm;
  delete form.railLengthInMm;
  delete form.cutSizeLeftInMm;
  delete form.cutSizeRightInMm;
  delete form.cutSizeLeftLiningInMm;
  delete form.cutSizeRightLiningInMm;
  delete form.headingHeightInMm;
  delete form.embraceLengthLeftInMm;
  delete form.embraceLengthRightInMm;
  delete form.waveSizeInMm;
  delete form.actualFabricWidthInMm;
  delete form.liningFabricActualWidthInMm;
  delete form.requiredFabricInMm;
  delete form.requiredLiningFabricInMm;
  delete form.highestHeightInSetInMm;

  form.numberOfPanels = vue.getNumberOfPanels(form);

  const ticketNumber = item.ticketNumber;

  const sharesHalfPanelWith = item.familyOrderLines.find(
    fol => fol.halfPanelTo === ticketNumber || fol.halfPanelFrom === ticketNumber
  );
  form.shareHalfPanelWith = sharesHalfPanelWith && sharesHalfPanelWith.orderNumber;

  const sharesHangerWith = item.familyOrderLines.find(fol => fol.sharesParcel);
  form.sharesHangerWith = sharesHangerWith && sharesHangerWith.orderNumber;

  const embraceForOrderLine = item.familyOrderLines.find(fol => fol.tieBackFrom === ticketNumber);
  form.embraceForOrderNumber = embraceForOrderLine && embraceForOrderLine.orderNumber;

  form.embraceFromOrderLine = item.familyOrderLines.find(fol => fol.tieBackFor === ticketNumber);
  if (!form.embraceFromOrderLine && !!item.embraceFinishID) {
    form.embraceFromOrderLine = {
      embraceFinishName: item.embraceFinishName,
      embraceHeightInMm: item.embraceHeightInMm,
      embraceLengthLeftInMm: item.embraceLengthLeftInMm,
      embraceLengthRightInMm: item.embraceLengthRightInMm
    };
  }

  if (returnYN) {
    form.returnOrderLineYN = true;
  }
  return form;
}

export default getFormDefinition;

const layoutTabGeneral = {
  named: true,
  panels: [
    {
      caption: 'orderLine.panelGeneral',
      fields: [
        'productGroupDescription',
        'orderExceedance',
        'customerReference',
        'returnOrderLineYN',
        'groupReference',
        'groupTotal'
      ]
    },
    {
      caption: 'orderLine.panelFinish',
      fields: [
        'finishedHeight',
        'finishedWidthLeft',
        'finishedWidthRight',
        'railLength',
        'headingHeight',
        'waveSize',
        'operationSide',
        'ribsEqual',
        'highestInSet',
        'ribsType',
        'ribsSide',
        'numberOfRibs',
        'assemblyHeight',
        'numberOfSupports',
        'numberOfCords',
        'orderLineDetails',
        'embraceFromOrderLine',
        'embraceLengthLeft',
        'embraceLengthRight',
        'remarksOnTicket',
        'specials'
      ]
    },
    {
      caption: 'orderLine.panelStatus',
      fields: [
        'status',
        'orderLineStatusID',
        'validationMessage',
        'orderedAt',
        'createdAt',
        'estimatedDeliveryDate',
        'actualDeliveryDate',
        'transportOrderID'
      ]
    },
    {
      caption: 'orderLine.panelFabricInfo',
      fields: [
        'fabricProductID',
        'actualFabricWidthInCm',
        'patternHeightInMm',
        'numberOfPanels',
        'numberOfPanelsLeft',
        'numberOfPanelsRight',
        'cutSize',
        'requiredFabric',
        'fabricPurchasePrice',
        'fabricConsumerPrice'
      ]
    },
    {
      caption: 'orderLine.panelLiningFabricInfo',
      fields: [
        'liningFabricProductID',
        'liningFabricActualWidthInCm',
        'liningFabricPatternHeightInMm',
        'numberOfPanelsLining',
        'numberOfPanelsLeftLining',
        'numberOfPanelsRightLining',
        'cutSizeLining',
        'requiredLiningFabric',
        'liningFabricPurchasePrice',
        'liningFabricConsumerPrice'
      ]
    },
    {
      caption: 'orderLine.panelCustomerInfo',
      fields: [
        'consumerName',
        'room',
        'remarks',
        'internalRemarks',
        'takeLeftoverFabricYN',
        'numberOfParcels',
        'shareHalfPanelWith',
        'shareHangerWith',
        'embraceForOrderNumber',
        'storeID',
        'alternativePickUpStore',
        'deliverAtHomeYN',
        'consumerAddress'
      ]
    }
  ],
  sections: [
    {
      width: 50,
      fields: [
        'productGroupDescription',
        'orderExceedance',
        'customerReference',
        'returnOrderLineYN',
        'groupReference',
        'groupTotal',
        'finishedHeight',
        'finishedWidthLeft',
        'finishedWidthRight',
        'railLength',
        'headingHeight',
        'waveSize',
        'operationSide',
        'ribsEqual',
        'highestInSet',
        'ribsType',
        'ribsSide',
        'numberOfRibs',
        'assemblyHeight',
        'numberOfSupports',
        'numberOfCords',
        'orderLineDetails',
        'embraceFromOrderLine',
        'embraceLengthLeft',
        'embraceLengthRight',
        'remarksOnTicket',
        'specials',
        'consumerName',
        'room',
        'remarks',
        'internalRemarks',
        'takeLeftoverFabricYN',
        'numberOfParcels',
        'shareHalfPanelWith',
        'shareHangerWith',
        'embraceForOrderNumber',
        'storeID',
        'alternativePickUpStore',
        'deliverAtHomeYN',
        'consumerAddress'
      ]
    },
    {
      width: 50,
      fields: [
        'status',
        'orderLineStatusID',
        'validationMessage',
        'orderedAt',
        'createdAt',
        'estimatedDeliveryDate',
        'actualDeliveryDate',
        'transportOrderID',
        'fabricProductID',
        'actualFabricWidthInCm',
        'patternHeightInMm',
        'numberOfPanels',
        'numberOfPanelsLeft',
        'numberOfPanelsRight',
        'cutSize',
        'requiredFabric',
        'fabricPurchasePrice',
        'fabricConsumerPrice',
        'liningFabricProductID',
        'liningFabricActualWidthInCm',
        'liningFabricPatternHeightInMm',
        'numberOfPanelsLining',
        'numberOfPanelsLeftLining',
        'numberOfPanelsRightLining',
        'cutSizeLining',
        'requiredLiningFabric',
        'liningFabricPurchasePrice',
        'liningFabricConsumerPrice'
      ]
    }
  ]
};

const layoutTabFamilyOrder = {
  named: true,
  sections: [
    {
      width: 100,
      fields: ['familyOrderLines']
    }
  ]
};

const layoutTabPrices = {
  named: true,
  sections: [
    {
      width: 100,
      fields: ['orderLinePrices']
    }
  ]
};

const layoutTabReturn = {
  named: true,
  visible: form => form.returnOrderLineYN,
  sections: [
    {
      width: 100,
      fields: ['returnCauseID', 'returnType', 'orderLineReturnReasonSelection']
    }
  ]
};

const layoutTabHistory = {
  named: true,
  sections: [
    {
      width: 100,
      fields: ['orderLineLogs']
    }
  ]
};

export function getCreateLayoutDefinition(vue) {
  return {
    groups: [layoutTabGeneral, layoutTabFamilyOrder, layoutTabReturn]
  };
}

export function getEditLayoutDefinition(vue) {
  return { groups: [layoutTabGeneral, layoutTabFamilyOrder, layoutTabReturn, layoutTabPrices, layoutTabHistory] };
}
